import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, IconButton, Tooltip,
} from '@material-ui/core';
import { Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@material-ui/icons';
import DeviceToolbar from './components/DeviceToolbar';
import styles from './ConfigStepLayout.module.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import LoadingState from 'components/kea/LoadingState';

/**
 * ConfigStepLayout - Reusable two-column layout for config steps
 * Left: Settings panel, Right: Preview panel
 * Used across: SearchBox, ResultsPage, Dropdown configs
 */
const ConfigStepLayout = ({
  title,
  description,
  loading,
  error,
  currentScreen,
  currentDevice,
  screens,
  onScreenChange,
  onDeviceChange,
  settingsPanel,
  previewPanel,
  isExpanded = false,
  onExpandToggle,
}) => {
  const t = useLocalizedMessage();

  React.useEffect(() => {
    if (!isExpanded || !onExpandToggle) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onExpandToggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, onExpandToggle]);

  if (loading) {
    return (
      <LoadingState
        variant="panel"
        label={<IntlMessages id="ui.integrationWizard.embedded.layout.configStepLayout.loadingConfiguration" />}
      />
    );
  }

  if (error) {
    return (
      <Box className={styles.loading}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      className={`${styles.root} ${isExpanded ? styles.expandedRoot : ''}`}
      data-testid="config-step-layout"
      data-expanded={isExpanded ? 'true' : 'false'}
    >
      <DeviceToolbar
        title={title}
        description={description}
        currentScreen={currentScreen}
        currentDevice={currentDevice}
        screens={screens}
        onScreenChange={onScreenChange}
        onDeviceChange={onDeviceChange}
      />

      <Box className={styles.workspace}>
        <Box component="aside" className={styles.settingsColumn} data-testid="config-settings-panel">
          {settingsPanel}
        </Box>

        <Box component="section" className={styles.previewColumn} data-testid="config-preview-panel">
          {onExpandToggle && (
            <Tooltip
              title={t(isExpanded ? 'common.exitFullscreen' : 'common.clickToZoom')}
              placement="left"
            >
              <IconButton
                className={styles.expandButton}
                onClick={onExpandToggle}
                aria-label={t(isExpanded ? 'common.exitFullscreen' : 'common.expandPreview')}
              >
                {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          )}
          <Box className={styles.previewStage}>
            {previewPanel}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

ConfigStepLayout.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.string,
  currentScreen: PropTypes.string,
  currentDevice: PropTypes.string,
  screens: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
    }),
  ),
  onScreenChange: PropTypes.func,
  onDeviceChange: PropTypes.func,
  settingsPanel: PropTypes.node.isRequired,
  previewPanel: PropTypes.node.isRequired,
  isExpanded: PropTypes.bool,
  onExpandToggle: PropTypes.func,
};

ConfigStepLayout.defaultProps = {
  title: null,
  description: null,
  loading: false,
  error: null,
  currentScreen: null,
  currentDevice: null,
  screens: null,
  onScreenChange: null,
  onDeviceChange: null,
  isExpanded: false,
  onExpandToggle: null,
};

export default ConfigStepLayout;
